// src/components/demo/DemoFleetBoard.js
//
// Fleet board (SPEC §4.4 / §8.3 screen 9): OUT / AVAILABLE / MAINTENANCE
// groups, live via Firestore listeners. Overdue rows go orange. Tap a row
// to jump straight to that board's card (check-in / check-out).

import React, { useState } from 'react';
import { FScreen, FHeader, FHeadline, FBtn, FLabel } from '../foam/FoamPrimitives';

function elapsedMins(checkedOutAt) {
  const t = checkedOutAt?.toDate ? checkedOutAt.toDate() : null;
  return t ? Math.max(0, Math.round((Date.now() - t.getTime()) / 60000)) : 0;
}

// Overdue rows flip to a FULL orange background (design: the one place a whole
// row goes orange, because overdue is the one thing staff must not miss).
const Row = ({ title, sub, right, overdue, muted, onClick, last }) => (
  <button type="button" onClick={onClick} className="text-left w-full" style={{ minHeight: 64 }}>
    <div
      className="flex justify-between items-center"
      style={{
        background: overdue ? 'var(--foam-orange)' : 'var(--foam-paper)',
        padding: '13px 14px',
        borderBottom: last ? 'none' : '1px solid var(--foam-hair)',
      }}
    >
      <div>
        <div
          className="font-model uppercase text-[18px]"
          style={{ color: overdue ? 'var(--foam-carbon)' : muted ? 'var(--foam-mute)' : 'var(--foam-ink)' }}
        >
          {title}
        </div>
        {sub && (
          <div className="font-sans text-[12px] mt-[3px]" style={{ color: overdue ? '#5a1a00' : 'var(--foam-ink-2)' }}>
            {sub}
          </div>
        )}
      </div>
      <span
        className="font-mono text-[12px]"
        style={{ color: overdue ? 'var(--foam-carbon)' : 'var(--foam-ink-2)', fontWeight: overdue ? 700 : 400 }}
      >
        {right}{overdue ? ' ⚠' : ''}
      </span>
    </div>
  </button>
);

const Group = ({ title, count, children }) => (
  <div>
    <div className="flex justify-between items-center pb-[8px]">
      <FLabel>{title}</FLabel>
      <FLabel color="var(--foam-mute)">{count}</FLabel>
    </div>
    <div style={{ border: '1.5px solid var(--foam-line)' }}>{children}</div>
  </div>
);

export default function DemoFleetBoard({ spot, dateLabel, fleet, openSessions, canReport, onEmailReport, onPick, onBack }) {
  const [reportState, setReportState] = useState(null); // null | 'sending' | 'sent' | 'error'
  const overdueAfter = spot.overdueMinutes || 60;
  const bySku = Object.fromEntries(openSessions.map((s) => [s.sku, s]));

  const out = fleet.filter((b) => b.status === 'out');
  const available = fleet.filter((b) => b.status === 'available')
    .sort((a, b) => (a.size || '').localeCompare(b.size || '', undefined, { numeric: true }));
  const maintenance = fleet.filter((b) => b.status === 'maintenance');

  return (
    <FScreen
      header={
        <>
          <FHeader right={<FLabel>◈ {spot.name}{dateLabel ? ` · ${dateLabel}` : ''}</FLabel>} />
          <div className="flex justify-between items-end mt-[16px]">
            <FHeadline size={44}>Fleet</FHeadline>
            <FLabel>{out.length} out · {available.length} in</FLabel>
          </div>
        </>
      }
      footer={
        <>
          {canReport && (
            <FBtn
              ghost
              disabled={reportState === 'sending'}
              onClick={async () => {
                setReportState('sending');
                try { await onEmailReport(); setReportState('sent'); }
                catch { setReportState('error'); }
              }}
            >
              {reportState === 'sending' ? 'Sending report…'
                : reportState === 'sent' ? 'Report sent — check your inbox'
                : reportState === 'error' ? 'Report failed — try again'
                : "Email me the day's report"}
            </FBtn>
          )}
          <FBtn ghost onClick={onBack}>Back to scan</FBtn>
        </>
      }
    >
      {fleet.length === 0 && (
        <p className="font-sans text-[15px]" style={{ color: 'var(--foam-ink-2)' }}>
          Nothing in today's fleet yet — scan a board to add it.
        </p>
      )}

      {out.length > 0 && (
        <Group title="Out" count={out.length}>
          {out.map((b, i) => {
            const s = bySku[b.sku];
            const mins = s ? elapsedMins(s.checkedOutAt) : 0;
            return (
              <Row
                key={b.id}
                title={`${b.productName} ${b.size}`.toUpperCase()}
                sub={`${s ? `${s.riderFirstName} ${s.riderLastName}` : ''}${b.loaner ? ' · loaner' : ''}`}
                right={mins >= 60 ? `${Math.floor(mins / 60)}h ${mins % 60}m` : `${mins}m`}
                overdue={mins >= overdueAfter}
                onClick={() => onPick(b.sku)}
                last={i === out.length - 1}
              />
            );
          })}
        </Group>
      )}

      {available.length > 0 && (
        <Group title="Available" count={available.length}>
          {available.map((b, i) => (
            <Row
              key={b.id}
              title={`${b.productName} ${b.size}`.toUpperCase()}
              sub={b.loaner ? `Loaner · ${b.loanerOwnerName || 'personal board'}` : null}
              right={b.sku.split('-').pop()}
              onClick={() => onPick(b.sku)}
              last={i === available.length - 1}
            />
          ))}
        </Group>
      )}

      {maintenance.length > 0 && (
        <Group title="Maintenance" count={maintenance.length}>
          {maintenance.map((b, i) => (
            <Row
              key={b.id}
              title={`${b.productName} ${b.size}`.toUpperCase()}
              sub={b.maintenanceNote || ''}
              right="⚑"
              muted
              onClick={() => onPick(b.sku)}
              last={i === maintenance.length - 1}
            />
          ))}
        </Group>
      )}
    </FScreen>
  );
}
